/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { forwardRef, useEffect, useState } from "react";

import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import { useStyles as sharedStyles } from "../../shared/styles/sharedStyles";
import Checkbox from "../CheckBox/Checkbox";
import { useStyles } from "./styles";
import { ListChoiceProps, Ref } from "./types";

const ListChoice = forwardRef<Ref, ListChoiceProps>(
  (
    {
      title,
      timeStamp,
      description,
      isCheckbox,
      iconLeft,
      iconRight,
      normalWhiteSpace,
      onClick,
      isLoading,
      alignIconCenter,
      ...props
    },
    ref
  ) => {
    const { blue200, gray100, gray200, gray700, textDark, textDisabled } =
      colors;

    const [isSelected, setIsSelected] = useState(Boolean(props.selected));

    const { body3Regular, body4Medium } = sharedStyles();
    const {
      iconLeftCSS,
      listChoiceContent,
      checkboxDiv,
      downloadProgressArea
    } = useStyles({
      alignIconCenter
    });

    useEffect(() => {
      setIsSelected(Boolean(props.selected));
    }, [props.selected]);

    const handleSelect = (e?: any) => {
      e?.stopPropagation();
      if (props.selected) {
        setIsSelected(!isSelected);
      }

      onClick?.();
    };

    return (
      <MenuItem
        {...props}
        ref={ref}
        selected={props.selected && isSelected}
        onClick={handleSelect}
        disabled={props.disabled || isLoading}
        sx={{
          color: textDark,
          alignItems: "center",
          padding:
            isCheckbox || iconLeft || iconRight ? "12px 16px" : "14px 16px",
          whiteSpace: normalWhiteSpace ? "normal" : "nowrap",
          wordBreak: normalWhiteSpace ? "break-word" : "normal",
          transition:
            "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;",

          "&.Mui-selected": {
            background: isCheckbox ? "unset" : blue200,

            "&:hover": {
              background: isCheckbox ? gray100 : blue200
            }
          },

          "&.Mui-disabled": {
            color: textDisabled,
            opacity: "1"
          },

          "&:hover": {
            backgroundColor: gray100
          },

          "&:active": {
            backgroundColor: gray200
          }
        }}
      >
        {iconLeft && <div css={css(iconLeftCSS)}>{iconLeft}</div>}

        <div css={css(listChoiceContent)}>
          {timeStamp && (
            <Typography
              css={css([{ color: textDark, marginBottom: "4px" }, body4Medium])}
            >
              {timeStamp}
            </Typography>
          )}

          <Typography
            variant="body2Medium"
            css={css({
              color: props.disabled || isLoading ? textDisabled : "inherit"
            })}
          >
            {title}
          </Typography>

          {description && (
            <Typography
              css={css([
                {
                  color: props.disabled || isLoading ? textDisabled : gray700,
                  whiteSpace: "normal"
                },
                body3Regular
              ])}
            >
              {description}
            </Typography>
          )}
        </div>

        {isLoading && (
          <div css={css(downloadProgressArea)}>
            <CircularProgress size={20} />
          </div>
        )}
        <div css={css(checkboxDiv)}>
          {iconRight}

          {isCheckbox && (
            <Checkbox checked={isSelected} disabled={props.disabled} />
          )}
        </div>
      </MenuItem>
    );
  }
);

export default ListChoice;
